import { useState, useEffect, useRef } from 'react'
import { Search, Check, X } from 'lucide-react'

/**
 * Editor for `editorType: node-ref` fields. Autocomplete over Drupal nodes
 * via `/api/builder/search/nodes?q=...&bundles=...`.
 *
 * Value shape :   {nid: int, bundle: string, label: string}
 *
 * Props :
 *  - value : current ref ({nid,bundle,label}) or null
 *  - bundles : array of allowed bundle machine names (passed from field def)
 *  - label : field label (for the modal header — provided by ModalFieldEditor)
 *  - onSave, onClose : standard
 */
export const NodeRefEditor = ({ value, bundles, onSave, onClose }) => {
  const [q, setQ] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState(
    value && typeof value === 'object' && value.nid ? value : null,
  )
  const debounceRef = useRef(null)

  const bundlesParam = Array.isArray(bundles) && bundles.length > 0
    ? `&bundles=${encodeURIComponent(bundles.join(','))}`
    : ''

  // Debounced search. Empty `q` returns the most recent matches.
  useEffect(() => {
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetch(
          `/api/builder/search/nodes?q=${encodeURIComponent(q)}${bundlesParam}&limit=20`,
          { credentials: 'include' },
        )
        if (res.ok) setResults(await res.json())
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 200)
    return () => clearTimeout(debounceRef.current)
  }, [q, bundlesParam])

  const handleSave = () => {
    if (selected) onSave(selected)
    onClose()
  }

  const handleClear = () => {
    setSelected(null)
    onSave(null)
    onClose()
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Search box */}
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={bundles?.length ? `Search ${bundles.join(' / ')}…` : 'Search nodes…'}
          autoFocus
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500"
        />
      </div>

      {/* Current selection */}
      {selected && (
        <div className="flex items-center justify-between p-2 bg-teal-50 border border-teal-200 rounded-lg">
          <div className="text-sm">
            <div className="font-medium text-teal-900">{selected.label}</div>
            <div className="text-xs text-teal-700">
              nid: {selected.nid} · {selected.bundle}
            </div>
          </div>
          <button
            onClick={() => setSelected(null)}
            className="p-1 rounded hover:bg-teal-100"
            aria-label="Remove selection"
          >
            <X size={14} className="text-teal-700" />
          </button>
        </div>
      )}

      {/* Results list */}
      <div className="border border-gray-100 rounded-lg max-h-72 overflow-y-auto">
        {loading && <p className="text-xs text-gray-400 text-center py-3">Loading…</p>}
        {!loading && results.length === 0 && (
          <p className="text-xs text-gray-400 text-center py-3">No matches.</p>
        )}
        {!loading && results.map((r) => (
          <button
            key={r.nid}
            type="button"
            onClick={() => setSelected(r)}
            className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between ${
              selected?.nid === r.nid
                ? 'bg-teal-50 text-teal-900'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="truncate">{r.label}</div>
              <div className="text-xs text-gray-400">
                nid: {r.nid} · {r.bundle}
              </div>
            </div>
            {selected?.nid === r.nid && <Check size={14} className="text-teal-600 flex-shrink-0" />}
          </button>
        ))}
      </div>

      {/* Actions */}
      <div className="builder-editor-actions builder-editor-actions-split">
        <button
          onClick={handleClear}
          className="btn btn-sm btn-ghost builder-editor-remove"
        >
          Retirer la référence
        </button>
        <div className="flex gap-2">
          <button onClick={onClose} className="btn btn-secondary">
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={!selected}
            className="btn btn-primary"
          >
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  )
}
